import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { renderAsync } from 'docx-preview';
import { Info, Loader } from 'lucide-react';

import { ConfirmationModal } from '@/components/core';
import { useToast } from '@/hooks/use-toast';
import {
  applyFloatingImageFixups,
  ensureWordFontFaces,
  parseDocxAnchors,
} from '@/lib/docx/render-fidelity';
import { applyTextEdit, TextEditError } from '@/lib/docx/apply-text-edit';
import { convertDocxToPdfBytes } from '@/modules/templates/services/docx-convert.service';
import { uploadDocxToStorage } from '@/modules/templates/services/docx-storage.service';
import { detectSignatureLocations, type AnchorInput } from '@/lib/pdf/detect-signature-anchors';
import { updateContractDocument } from '@/modules/org/contracts/services/contract-document.service';
import { useGetContractDocumentEvents } from '@/modules/org/contracts/hooks/use-contract-document-events';
import { orgContractDocumentByIdQueryKey } from '@/graphql';
import {
  parseSignatories,
  serializeSignatories,
  type ContractSignatory,
  type OrgContractDocumentItem,
} from '@/modules/org/contracts/types/contract-document.types';

import { PdfPreviewToolbar } from '@/modules/templates/components/review/PdfPreviewToolbar';
import PreparingContract from '@/modules/templates/components/review/PreparingContract';
import { UnsavedChangesDialog } from '@/modules/templates/components/review/UnsavedChangesDialog';
import type { SignatureField } from '@/modules/templates/components/review/SignatureFieldOverlay';
import { ReviewShell } from '@/modules/templates/components/review/ReviewShell';
import { ReviewSidebar } from '@/modules/templates/components/review/ReviewSidebar';
import { SidebarFooter } from '@/modules/templates/components/review/SidebarFooter';
import { MobileActionBar } from '@/modules/templates/components/review/MobileActionBar';
import TemplatePDFReviewHeader from '@/modules/templates/components/review/TemplatePDFReviewHeader';
import type {
  ReviewBlock,
  ReviewOutlineNode,
} from '@/modules/templates/components/review/adapter/types';
import { DOCX_MIME } from '../utils';
import { useDocxHistory } from './useDocxHistory';
import { buildDocxOutline, isDocxBlockRef } from './docx-outline';
import {
  blockElementById,
  describeBlock,
  resolveBlockElement,
  type DocxBlockInfo,
} from './docx-block-model';
import {
  applyPlacements,
  buildFieldsFromSignatories,
  colorForIndex,
  defaultFieldPair,
} from './signature-fields';
import { DocxPageOverlayLayer } from './DocxPageOverlayLayer';
import { useDocxPageGeometry } from './useDocxPageGeometry';
import { seedDocxFields, type UnanchoredSignatory } from './docx-field-seed';
import { sendDocxForSignature } from './docx-signature-send';
import { parseDocxDocument, type FixedDocxDocument } from './document-payload';
import type { Rect } from '@/modules/org/components/docx-builder/selection/types';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const PERSIST_DEBOUNCE_MS = 900;

/**
 * `?anchors=1` outlines the text each signature box was derived from.
 *
 * A misplaced box has two possible causes that look identical on screen: the
 * anchor search found the wrong text, or it found the right text and the box
 * was positioned badly. Drawing the anchor itself tells them apart at a glance.
 */
const SHOW_ANCHOR_DEBUG =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('anchors');

interface AnchorDebugRect {
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface DocxEditableReviewProps {
  contract: OrgContractDocumentItem;
  documentId: string;
  slug: string;
  /** The merged/edited .docx bytes to render and edit (source of truth). */
  initialDocxBytes: Uint8Array;
  hideDocumentActions?: boolean;
}

/**
 * Editable fixed-DOCX review. Renders the merged .docx as HTML (docx-preview) with the same
 * feature set as the AI review: a Details/Edit tabbed sidebar, a clickable document outline,
 * hover-to-highlight + click-to-edit, undo/redo history, and an unsaved-changes guard. Each
 * edit is written back into the .docx XML and re-rendered instantly (no server round-trip).
 * A PDF is produced only at download / continue-to-signing, where signature positions are
 * re-detected on the freshly converted PDF and the PDF-based signing surface takes over.
 */
export default function DocxEditableReview({
  contract,
  documentId,
  slug,
  initialDocxBytes,
  hideDocumentActions = false,
}: DocxEditableReviewProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showApprovalConfirmation, setShowApprovalConfirmation] = useState(false);
  const [isContractApproved, setIsContractApproved] = useState(contract.IsApproved === true);
  const [isApprovingContract, setIsApprovingContract] = useState(false);
  const templateTitle = contract.TemplateTitle || contract.Name || 'Document';
  const isPendingApproval = contract.HasSentForApproval === true && !isContractApproved;
  const isEditDisabled =
    (hideDocumentActions && isContractApproved) ||
    (!hideDocumentActions && contract.HasSentForApproval === true);

  useEffect(() => {
    setIsContractApproved(contract.IsApproved === true);
  }, [contract.IsApproved]);

  const handleApproveContract = useCallback(async () => {
    if (isApprovingContract) return;

    try {
      setIsApprovingContract(true);
      await updateContractDocument(documentId, { IsApproved: true });
      void queryClient.invalidateQueries({
        queryKey: orgContractDocumentByIdQueryKey(documentId),
      });
      setIsContractApproved(true);
      setShowApprovalConfirmation(false);
      toast({
        variant: 'success',
        title: t('GENERATEREVIEW_TOAST_CONTRACTAPPROVED'),
        description: t('GENERATEREVIEW_TOAST_CONTRACTAPPROVED_SUCCESSFULLY'),
      });
    } catch (error) {
      console.error('Failed to approve contract', error);
      toast({
        variant: 'destructive',
        title: 'Unable to approve contract',
        description: 'Please try again.',
      });
    } finally {
      setIsApprovingContract(false);
    }
  }, [documentId, isApprovingContract, queryClient, t, toast]);

  // The .docx bytes are the source of truth; editing/undo/redo replace them and re-render.
  const {
    current: docxBytes,
    canUndo,
    canRedo,
    push: pushHistory,
    undo: undoHistory,
    redo: redoHistory,
  } = useDocxHistory(initialDocxBytes);

  // Viewer state. `rendering` covers only the very first paint (there is no
  // previous frame to keep on screen, so it earns the full loader).
  const [rendering, setRendering] = useState(true);
  // A background re-render after an edit/undo/redo. The previous render stays
  // fully visible while the new one paints into a hidden buffer — mirrors the
  // PDF review's double-buffered preview (usePdfPreview), so no loader and no
  // blank flash; just the toolbar's "Preview updating…" chip.
  const [isRerendering, setIsRerendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [fitScale, setFitScale] = useState(1);
  const [userZoom, setUserZoom] = useState(1);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [outline, setOutline] = useState<ReviewOutlineNode[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('details');

  // Hover + edit state.
  const [hoverRect, setHoverRect] = useState<Rect | null>(null);
  const [editingBlock, setEditingBlock] = useState<DocxBlockInfo | null>(null);
  const [editRect, setEditRect] = useState<Rect | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editedBlockIds, setEditedBlockIds] = useState<Set<string>>(new Set());
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [pendingBlockId, setPendingBlockId] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditDisabled) return;
    setSidebarTab('details');
    setEditingBlock(null);
    setEditRect(null);
    setHoverRect(null);
  }, [isEditDisabled]);

  // Draggable signature fields over the rendered docx, in PDF points.
  const [signatureFields, setSignatureFields] = useState<SignatureField[]>([]);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  // Fields the user has moved: their placement wins over anchor re-detection.
  const touchedFieldIds = useRef<Set<string>>(new Set());
  const [isPreparing, setIsPreparing] = useState(false);
  // Download is tracked apart from `isPreparing`: sending for signature is a
  // long multi-step flow that earns a blocking overlay, whereas a download is
  // one conversion the user should be able to wait out with the page still
  // usable. It gets a spinner on the button instead.
  const [isDownloading, setIsDownloading] = useState(false);
  // Separate from `isDownloading`: this path converts the .docx *and* rasterises
  // every page, so it is much the slower of the two and needs its own state.
  const [isDownloadingProtected, setIsDownloadingProtected] = useState(false);
  const [anchorDebug, setAnchorDebug] = useState<AnchorDebugRect[]>([]);
  const [unanchored, setUnanchored] = useState<UnanchoredSignatory[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Edit-tab scroller, so the shared sidebar can restore its scroll position.
  const editTabScrollRef = useRef<HTMLDivElement>(null);
  const scaleWrapRef = useRef<HTMLDivElement>(null);
  const docRef = useRef<HTMLDivElement>(null);
  // The positioned box the (unscaled) signature-overlay layer lives in.
  const overlayRootRef = useRef<HTMLDivElement>(null);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const placementTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors `signatureFields` so a drag can diff against the current boxes
  // synchronously — pointermove fires faster than React re-renders.
  const signatureFieldsRef = useRef<SignatureField[]>([]);
  const restoreScrollRef = useRef<number | null>(null);
  // True once a render has completed — picks loader vs seamless-buffer path.
  const hasRenderedRef = useRef(false);
  // Resolves once the in-flight render has swapped in, so a save can hold its
  // "Saving…" spinner until the updated document is actually on screen.
  const renderDoneRef = useRef<(() => void) | null>(null);
  // Editor dirty/text tracking so we can guard block switches mid-edit.
  const editorDirtyRef = useRef(false);
  const editorTextRef = useRef('');
  // Holds the block targeted by a switch while the unsaved-changes dialog is open.
  const pendingRef = useRef<DocxBlockInfo | null>(null);

  const appliedScale = fitScale * userZoom;
  const isEditing = editingBlock !== null;

  const signatories = useMemo(() => parseSignatories(contract.Signatories), [contract.Signatories]);

  // Shape the signatories the way the shared signature overlay expects.
  const overlaySignatories = useMemo(
    () =>
      signatories.map((s, i) => ({
        name: s.name,
        email: s.email,
        role: s.title ?? '',
        color: colorForIndex(i),
      })),
    [signatories]
  );

  // Per-signatory signing events (for the Details tab status chips).
  const { data: eventsResult } = useGetContractDocumentEvents(documentId, !!documentId);
  const signatoryEventByEmail = useMemo(() => {
    const map = new Map<string, { eventType: string; occurredAt: string; signedTime?: string }>();
    (eventsResult?.items ?? []).forEach((e) => {
      if (
        (e.EventType !== 'signatory_signed_success' && e.EventType !== 'signatory_signed_failed') ||
        !e.SignatoryEmail
      ) {
        return;
      }
      const existing = map.get(e.SignatoryEmail);
      const occurredAt = e.OccurredAt ?? e.CreatedDate ?? '';
      if (!existing || occurredAt > existing.occurredAt) {
        map.set(e.SignatoryEmail, { eventType: e.EventType, occurredAt, signedTime: e.SignedTime });
      }
    });
    return map;
  }, [eventsResult?.items]);

  /** Scroll a page into view from the sidebar's page mini-map. */
  const scrollToPage = useCallback((page: number) => {
    const root = docRef.current;
    const section = root?.querySelectorAll<HTMLElement>('section.docxpv')[page - 1];
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const measureAndStore = useCallback(() => {
    const el = docRef.current;
    if (!el) return;
    const wrapper = el.firstElementChild as HTMLElement | null;
    const w = wrapper?.scrollWidth || el.scrollWidth || 0;
    const h = wrapper?.scrollHeight || el.scrollHeight || 0;
    if (w <= 0 || h <= 0) return;
    setNaturalSize((prev) =>
      prev && Math.abs(prev.w - w) <= 1 && Math.abs(prev.h - h) <= 1 ? prev : { w, h }
    );
  }, []);

  // --- Render the .docx whenever the bytes change --------------------------
  //
  // The first render paints straight into the visible container behind the
  // full-screen loader. Every later render (edit/undo/redo) paints into a
  // hidden-but-laid-out sibling buffer and is swapped in atomically once
  // fonts, images and fixups have settled — the previous render stays fully
  // visible for the whole regeneration, exactly like the PDF review's
  // double-buffered preview (usePdfPreview): no loader, no blank flash.
  useEffect(() => {
    const el = docRef.current;
    if (!el) return;
    let cancelled = false;
    const initial = !hasRenderedRef.current;

    let target: HTMLElement = el;
    let buffer: HTMLDivElement | null = null;
    if (initial) {
      el.innerHTML = '';
      setRendering(true);
      setNaturalSize(null);
    } else {
      // The buffer lives beside the visible render inside the same scaled
      // wrapper, so the geometry-dependent fixups measure identical layout.
      // `visibility: hidden` (not display:none) keeps that layout live.
      buffer = window.document.createElement('div');
      Object.assign(buffer.style, {
        position: 'absolute',
        left: '0',
        top: '0',
        visibility: 'hidden',
        pointerEvents: 'none',
      });
      el.parentElement?.appendChild(buffer);
      target = buffer;
      setIsRerendering(true);
    }
    setRenderError(null);
    setHoverRect(null);

    const onFontsDone = () => {
      if (!cancelled) {
        measureAndStore();
        setLayoutVersion((v) => v + 1);
      }
    };

    (async () => {
      try {
        ensureWordFontFaces();
        const anchors = parseDocxAnchors(docxBytes);
        // docx-preview detaches the buffer it reads; hand it a copy so `docxBytes` stays usable.
        await renderAsync(new Uint8Array(docxBytes), target, undefined, {
          className: 'docxpv',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          useBase64URL: true,
          experimental: true,
        });
        if (cancelled) return;
        try {
          await document.fonts.ready;
        } catch {
          /* fonts API unavailable — measure anyway */
        }
        await Promise.all(
          Array.from(target.querySelectorAll('img')).map((img) =>
            (img as HTMLImageElement).decode().catch(() => undefined)
          )
        );
        if (cancelled) return;
        applyFloatingImageFixups(target, anchors);
        // Buffered path: everything is decoded and fixed up, so moving the
        // children across is one synchronous DOM operation — no blank frame.
        if (buffer) {
          el.replaceChildren(...Array.from(buffer.childNodes));
          buffer.remove();
          buffer = null;
        }
        hasRenderedRef.current = true;
        measureAndStore();
        setLayoutVersion((v) => v + 1);
        // Restore the pre-edit scroll offset once the new render has laid out.
        if (restoreScrollRef.current != null) {
          const top = restoreScrollRef.current;
          restoreScrollRef.current = null;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (!cancelled && scrollRef.current) scrollRef.current.scrollTop = top;
            });
          });
        }
      } catch {
        if (!cancelled) {
          if (initial) {
            setRenderError('Could not render the document preview.');
          } else {
            // The previous render is still on screen and still valid: keep it
            // instead of tearing the viewer down to an error state.
            toast({
              variant: 'destructive',
              title: 'Preview not updated',
              description: 'Could not re-render the document preview. Please try again.',
            });
          }
        }
      } finally {
        buffer?.remove();
        if (!cancelled) {
          if (initial) setRendering(false);
          else setIsRerendering(false);
        }
        // Release anyone holding a spinner on this render (e.g. Save).
        const resolve = renderDoneRef.current;
        renderDoneRef.current = null;
        resolve?.();
      }
    })();

    document.fonts?.addEventListener?.('loadingdone', onFontsDone);
    return () => {
      cancelled = true;
      buffer?.remove();
      document.fonts?.removeEventListener?.('loadingdone', onFontsDone);
    };
  }, [docxBytes, measureAndStore, toast]);

  // --- Fit-to-width -------------------------------------------------------
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !naturalSize || naturalSize.w === 0) return;
    const compute = () => setFitScale(Math.min(1, (container.clientWidth - 24) / naturalSize.w));
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [naturalSize]);

  // Rebuild the outline + page count after each (re-)render.
  useEffect(() => {
    const root = docRef.current;
    if (!root || rendering) return;
    setOutline(buildDocxOutline(root));
    setNumPages(root.querySelectorAll('section.docxpv').length || null);
  }, [layoutVersion, rendering]);

  useEffect(
    () => () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      if (placementTimerRef.current) clearTimeout(placementTimerRef.current);
    },
    []
  );

  // Convert a block element's client rect into scale-independent coords inside scaleWrap.
  const rectForBlock = useCallback(
    (block: HTMLElement): Rect | null => {
      const wrap = scaleWrapRef.current;
      if (!wrap) return null;
      const box = wrap.getBoundingClientRect();
      const s = appliedScale || 1;
      const r = block.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return null;
      const pad = 4;
      return {
        x: (r.left - box.left) / s - pad,
        y: (r.top - box.top) / s - pad,
        w: r.width / s + pad * 2,
        h: r.height / s + pad * 2,
      };
    },
    [appliedScale]
  );

  // Recompute the persistent edit-highlight rect after a re-render/reflow.
  useEffect(() => {
    if (!editingBlock) return;
    const rect = rectForBlock(editingBlock.element);
    if (rect) setEditRect(rect);
  }, [layoutVersion, appliedScale, editingBlock, rectForBlock]);

  // Per-page geometry for the signature overlay (position + true point size).
  const pageGeometry = useDocxPageGeometry({
    docRef,
    containerRef: overlayRootRef,
    layoutVersion,
    appliedScale,
    rendering,
  });

  // Seed signature fields once the pages are measured. Fields the user has
  // dragged are preserved verbatim across re-renders and zoom changes — only
  // untouched ones are re-derived from their anchors.
  useEffect(() => {
    if (rendering || pageGeometry.length === 0) return;
    const { fields: seeded, anchors, unanchored } = seedDocxFields({
      signatories,
      pages: pageGeometry,
      root: docRef.current,
      container: overlayRootRef.current,
      docxBytes,
    });
    setSignatureFields((prev) => {
      const next =
        touchedFieldIds.current.size === 0
          ? seeded
          : seeded.map(
            (field) =>
              prev.find((p) => p.id === field.id && touchedFieldIds.current.has(field.id)) ??
              field
          );
      signatureFieldsRef.current = next;
      return next;
    });
    setUnanchored(unanchored);

    // Anchor rects, converted into the scale wrapper's own (unscaled) space —
    // the same coordinate trick the template-marking canvas uses for its
    // highlights. Only computed when the diagnostic is switched on.
    if (!SHOW_ANCHOR_DEBUG) return;
    const wrap = scaleWrapRef.current;
    if (!wrap) return;
    const box = wrap.getBoundingClientRect();
    const s = appliedScale || 1;
    setAnchorDebug(
      anchors.map((a) => ({
        label: `${a.label} · ${a.tier}`,
        x: (a.rect.left - box.left) / s,
        y: (a.rect.top - box.top) / s,
        w: (a.rect.right - a.rect.left) / s,
        h: (a.rect.bottom - a.rect.top) / s,
      }))
    );
    // `signatories` is derived per render; key off what actually moves the boxes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutVersion, rendering, pageGeometry, contract.Signatories]);

  /**
   * Save the boxes the user dragged.
   *
   * Only their placements are written — an untouched box is re-derived from its
   * anchor on every render, so storing it would freeze a live value and make a
   * later send treat it as deliberate. Debounced, since a drag emits a change
   * per pointer move.
   */
  const persistPlacements = useCallback(
    (fields: SignatureField[]) => {
      if (!documentId) return;
      if (placementTimerRef.current) clearTimeout(placementTimerRef.current);
      placementTimerRef.current = setTimeout(async () => {
        try {
          const next = applyPlacements(signatories, fields, touchedFieldIds.current, {
            includeDetected: false,
          });
          await updateContractDocument(documentId, {
            Signatories: serializeSignatories(next),
          });
        } catch (err) {
          // Non-fatal: the placement is still correct on screen and is resent
          // from live state, so a failed save costs only cross-reload memory.
          console.error('Failed to save signature placement:', err);
        }
      }, PERSIST_DEBOUNCE_MS);
    },
    [documentId, signatories]
  );

  const handleSignatureFieldsChange = useCallback(
    (next: SignatureField[]) => {
      // Anything whose geometry changed (or that's newly added) counts as a
      // deliberate placement and must survive later anchor re-detection.
      const prev = signatureFieldsRef.current;
      let moved = false;
      next.forEach((field) => {
        const before = prev.find((p) => p.id === field.id);
        if (
          !before ||
          before.x !== field.x ||
          before.y !== field.y ||
          before.width !== field.width ||
          before.height !== field.height ||
          before.pageNumber !== field.pageNumber
        ) {
          touchedFieldIds.current.add(field.id);
          moved = true;
        }
      });
      signatureFieldsRef.current = next;
      setSignatureFields(next);
      if (moved) persistPlacements(next);
    },
    [persistPlacements]
  );

  const handleToggleSection = useCallback((id: string, open: boolean) => {
    setOpenSections((prev) => (prev[id] === open ? prev : { ...prev, [id]: open }));
  }, []);

  // --- Open / switch / close the editor -----------------------------------
  const openEditorForBlock = useCallback(
    (info: DocxBlockInfo) => {
      setEditingBlock(info);
      setEditRect(rectForBlock(info.element));
      setHoverRect(null);
      setSidebarTab('edit');
      setSidebarOpen(true);
      editorDirtyRef.current = false;
    },
    [rectForBlock]
  );

  const requestBlockSwitch = useCallback(
    (info: DocxBlockInfo) => {
      if (isEditDisabled) return;
      if (isEditing && editingBlock?.id === info.id) return;
      if (isEditing && editorDirtyRef.current) {
        // Stash the pending target; the unsaved-changes dialog resolves it.
        pendingRef.current = info;
        setPendingBlockId(info.id);
        return;
      }
      openEditorForBlock(info);
    },
    [isEditDisabled, isEditing, editingBlock, openEditorForBlock]
  );

  const closeEditor = useCallback(() => {
    setEditingBlock(null);
    setEditRect(null);
    editorDirtyRef.current = false;
  }, []);

  // Scroll a block to the centre of the viewport (used by outline navigation).
  const scrollBlockIntoView = useCallback((el: HTMLElement) => {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // --- Hover + click-to-edit on the document ------------------------------
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isEditing) return;
      const root = docRef.current;
      if (!root) return;
      const block = resolveBlockElement(e.target as Node, root);
      setHoverRect(block ? rectForBlock(block) : null);
    },
    [isEditing, rectForBlock]
  );

  const handleMouseLeave = useCallback(() => setHoverRect(null), []);

  const handleDocClick = useCallback(
    (e: React.MouseEvent) => {
      // While a re-render is in flight the visible DOM belongs to the previous
      // bytes — an editor opened from it could save stale text. (The loader
      // overlay used to block this window; the seamless swap no longer does.)
      if (isRerendering) return;
      const root = docRef.current;
      if (!root) return;
      const block = resolveBlockElement(e.target as Node, root);
      if (!block) return;
      const info = describeBlock(root, block);
      if (info) requestBlockSwitch(info);
    },
    [requestBlockSwitch, isRerendering]
  );

  // Outline click → scroll to + open the block. Outline entries carry only a
  // paragraph id (describing every block up front would be O(n²)), so the live
  // block is resolved here.
  const handleOutlineEdit = useCallback(
    (block: ReviewBlock) => {
      // Same stale-DOM guard as handleDocClick.
      if (isRerendering) return;
      const id = isDocxBlockRef(block.source) ? block.source.id : block.id;
      const root = docRef.current;
      if (!root) return;
      const el = blockElementById(root, id);
      if (!el) return;
      const info = describeBlock(root, el);
      if (!info) return;
      scrollBlockIntoView(el);
      requestBlockSwitch(info);
    },
    [requestBlockSwitch, scrollBlockIntoView, isRerendering]
  );

  // --- Persist edited .docx (debounced) -----------------------------------
  const persistDocx = useCallback(
    (bytes: Uint8Array) => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(async () => {
        try {
          const fileBase = slug ? slug.replace(/[^a-zA-Z0-9_-]/g, '_') : 'document';
          const { url, fileId } = await uploadDocxToStorage(bytes, `${fileBase}.edited.docx`);
          const existing: FixedDocxDocument = parseDocxDocument(contract.Document) ?? {};
          await updateContractDocument(documentId, {
            Document: JSON.stringify({
              ...existing,
              type: existing.type ?? 'fixed_docx',
              docxUrl: url,
              docxFileId: fileId,
            } satisfies FixedDocxDocument),
          });
        } catch (err) {
          console.error('Failed to persist edited .docx:', err);
          toast({
            variant: 'destructive',
            title: 'Saved locally',
            description: 'Could not sync your edit to the server. It will retry on your next edit.',
          });
        }
      }, PERSIST_DEBOUNCE_MS);
    },
    [slug, contract.Document, documentId, toast]
  );

  const applyEdit = useCallback(
    (block: DocxBlockInfo, newText: string) => {
      const updated = applyTextEdit(docxBytes, {
        originalText: block.text,
        newText,
        contextBefore: block.contextBefore,
        contextAfter: block.contextAfter,
      });
      restoreScrollRef.current = scrollRef.current?.scrollTop ?? null;
      pushHistory(updated);
      setEditedBlockIds((prev) => new Set(prev).add(block.id));
      persistDocx(updated);
    },
    [docxBytes, pushHistory, persistDocx]
  );

  const handleSaveEdit = useCallback(
    async (newText: string) => {
      if (!editingBlock) return;
      setIsSavingEdit(true);
      try {
        applyEdit(editingBlock, newText);
        // Hold the "Saving…" state until the re-rendered document has swapped
        // in — the editor closing is the user's cue that the edit is on
        // screen (same contract as the PDF review awaiting regenerateNow).
        await new Promise<void>((resolve) => {
          renderDoneRef.current?.();
          renderDoneRef.current = resolve;
        });
        closeEditor();
      } catch (err) {
        const message =
          err instanceof TextEditError
            ? err.message
            : 'Could not apply the edit. Please try again.';
        toast({ variant: 'destructive', title: "Couldn't save this edit", description: message });
      } finally {
        setIsSavingEdit(false);
      }
    },
    [editingBlock, applyEdit, closeEditor, toast]
  );

  const handleCancelEdit = useCallback(() => closeEditor(), [closeEditor]);

  // --- Unsaved-changes dialog ---------------------------------------------
  const clearPending = useCallback(() => {
    pendingRef.current = null;
    setPendingBlockId(null);
  }, []);

  const handleDiscardAndSwitch = useCallback(() => {
    const next = pendingRef.current;
    clearPending();
    editorDirtyRef.current = false;
    if (next) openEditorForBlock(next);
  }, [clearPending, openEditorForBlock]);

  const handleSaveAndSwitch = useCallback(() => {
    const next = pendingRef.current;
    if (editingBlock) {
      try {
        applyEdit(editingBlock, editorTextRef.current.trim());
      } catch (err) {
        const message = err instanceof TextEditError ? err.message : 'Could not apply the edit.';
        toast({ variant: 'destructive', title: "Couldn't save this edit", description: message });
        return;
      }
    }
    clearPending();
    editorDirtyRef.current = false;
    if (next) openEditorForBlock(next);
  }, [editingBlock, applyEdit, clearPending, openEditorForBlock, toast]);

  // --- Undo / redo --------------------------------------------------------
  const handleUndo = useCallback(() => {
    if (!canUndo) return;
    restoreScrollRef.current = scrollRef.current?.scrollTop ?? null;
    const prev = undoHistory();
    if (prev) persistDocx(prev);
  }, [canUndo, undoHistory, persistDocx]);

  const handleRedo = useCallback(() => {
    if (!canRedo) return;
    restoreScrollRef.current = scrollRef.current?.scrollTop ?? null;
    const next = redoHistory();
    if (next) persistDocx(next);
  }, [canRedo, redoHistory, persistDocx]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isEditing) return; // editor owns its own keys while open
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (isMod && (e.key === 'z' || e.key === 'Z') && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isEditing, handleUndo, handleRedo]);

  // --- Download -----------------------------------------------------------
  const handleDownload = useCallback(async () => {
    // Converting the .docx server-side takes seconds; without this the button
    // looks inert and gets clicked again, queuing a second conversion.
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const fileBase = slug ? slug.replace(/[^a-zA-Z0-9_-]/g, '_') : 'document';
      const pdfBytes = await convertDocxToPdfBytes(docxBytes, `${fileBase}.docx`);
      const objectUrl = window.URL.createObjectURL(
        new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
      );
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = `${fileBase}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Download failed:', err);
      toast({ variant: 'destructive', title: 'Error', description: 'Could not prepare the PDF.' });
    } finally {
      setIsDownloading(false);
    }
  }, [docxBytes, slug, toast, isDownloading]);

  /**
   * As above, then rebuilt as images so no text can be extracted. Two slow steps
   * back to back — a server-side .docx conversion followed by rasterising every
   * page — so on a long contract this can run for a while.
   */
  const handleProtectedDownload = useCallback(async () => {
    if (isDownloadingProtected) return;
    setIsDownloadingProtected(true);
    try {
      const fileBase = slug ? slug.replace(/[^a-zA-Z0-9_-]/g, '_') : 'document';
      const pdfBytes = await convertDocxToPdfBytes(docxBytes, `${fileBase}.docx`);
      // Imported on demand: this pulls in pdfjs plus the react-pdf renderer.
      const { buildProtectedPdf } = await import('@/lib/pdf/protected-pdf');
      const protectedBlob = await buildProtectedPdf(
        new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
      );
      const objectUrl = window.URL.createObjectURL(protectedBlob);
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = `${fileBase}-protected.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Protected download failed:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not prepare a protected PDF.',
      });
    } finally {
      setIsDownloadingProtected(false);
    }
  }, [docxBytes, slug, toast, isDownloadingProtected]);

  // --- Send for signature (all in the background) -------------------------
  const handleSendForSignature = useCallback(async () => {
    if (signatories.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No signatories',
        description: 'This document has no signatories to send to.',
      });
      return;
    }
    setIsPreparing(true);
    try {
      const fileBase = slug ? slug.replace(/[^a-zA-Z0-9_-]/g, '_') : 'document';
      // Convert the (edited) .docx to a PDF — the artifact the signature service
      // signs. Bytes only: nothing is written to storage, and the same buffer
      // feeds anchor detection and the rollout upload.
      const pdfBytes = await convertDocxToPdfBytes(
        new Blob([docxBytes as BlobPart], { type: DOCX_MIME }),
        `${fileBase}.docx`
      );

      // Re-detect each signatory's signature box on the freshly converted PDF (edits reflow
      // the document, so any previously stored coordinates are stale). Keyed by signatory id.
      const anchors: AnchorInput[] = signatories.flatMap((s) =>
        s.signatureAnchor?.originalText
          ? [
            {
              signatoryKey: s.id,
              originalText: s.signatureAnchor.originalText,
              contextBefore: s.signatureAnchor.contextBefore,
              contextAfter: s.signatureAnchor.contextAfter,
              paragraphIndex: s.signatureAnchor.paragraphIndex,
            },
          ]
          : []
      );
      const locations = anchors.length ? await detectSignatureLocations(pdfBytes, anchors) : {};

      // Anything the user dragged is their explicit intent and is sent as-is;
      // untouched boxes take the freshly-detected anchor position, since the
      // converted PDF may paginate differently from the preview.
      const detected: SignatureField[] = buildFieldsFromSignatories(
        signatories.map((s) => ({
          ...s,
          signatureLocation: locations[s.id] ?? null,
          dateLocation: null,
        }))
      );
      const fields: SignatureField[] = signatureFields.map((field) => {
        if (touchedFieldIds.current.has(field.id)) return field;
        return detected.find((d) => d.id === field.id) ?? field;
      });
      // Any signatory still without a signature box gets a default one.
      const placedIdx = new Set(
        fields.filter((f) => f.type === 'signature').map((f) => f.signatoryIndex)
      );
      signatories.forEach((_, i) => {
        if (!placedIdx.has(i)) {
          fields.push(...defaultFieldPair(i, numPages || 1, { widthPt: 612, heightPt: 792 }));
        }
      });

      // Store exactly what we're sending, tagged with which space each box came
      // from. Without the tag, reopening the review would replay PDF-space
      // coordinates onto the docx preview and land them on the wrong page.
      const nextSignatories: ContractSignatory[] = applyPlacements(
        signatories,
        fields,
        touchedFieldIds.current,
        { includeDetected: true }
      );

      // Persist the resolved placements *before* rolling out, but leave Status
      // alone: marking the document rolled-out up front meant a failed rollout
      // locked it permanently with nothing actually sent.
      await updateContractDocument(documentId, {
        Signatories: serializeSignatories(nextSignatories),
      });

      // Fire the SELISE rollout in the same call the PDF flow uses.
      await sendDocxForSignature({
        pdfBytes,
        documentId,
        templateSlug: slug,
        templateTitle,
        numPages: numPages || 1,
        signatureFields: fields,
        signatories: nextSignatories.map((s) => ({
          name: s.name,
          email: s.email,
          role: s.title ?? '',
          order: s.order ?? null,
        })),
      });

      // Only now is the document genuinely rolled out — lock it from editing.
      await updateContractDocument(documentId, { Status: 'rollout_success' });

      navigate(`/templates/${slug}/review/success?document=${encodeURIComponent(documentId)}`);
    } catch (err) {
      console.error('Failed to send for signature:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not send the document for signature.',
      });
      setIsPreparing(false);
    }
  }, [
    signatories,
    signatureFields,
    slug,
    docxBytes,
    numPages,
    documentId,
    templateTitle,
    navigate,
    toast,
  ]);

  const spacerHeight = naturalSize ? naturalSize.h * appliedScale : undefined;
  const spacerWidth = naturalSize ? naturalSize.w * appliedScale : undefined;

  // The block being edited, in the shared editor's normalized shape. `source`
  // round-trips the live DOM block so the save path can reach it.
  const editorBlock: ReviewBlock | null = editingBlock
    ? {
      id: editingBlock.id,
      text: editingBlock.text,
      kind: 'paragraph',
      isTitle: false,
      label: 'Paragraph',
      source: editingBlock,
    }
    : null;

  const sidebarInner = (
    <ReviewSidebar
      sidebarTab={sidebarTab}
      onTabChange={setSidebarTab}
      isEditable
      isEditDisabled={isEditDisabled}
      isPremiumUnlocked
      editedBlockIds={editedBlockIds}
      details={{ title: templateTitle }}
      signatureFields={signatureFields}
      numPages={numPages}
      signatories={overlaySignatories}
      signatoryEventByEmail={signatoryEventByEmail}
      currentPage={1}
      onPageClick={scrollToPage}
      editTabScrollRef={editTabScrollRef}
      isEditing={isEditing}
      editingBlock={editorBlock}
      isSavingEdit={isSavingEdit}
      isEditingEffectiveDate={false}
      onSaveEdit={handleSaveEdit}
      onCancelEdit={handleCancelEdit}
      onEditorDirtyChange={(dirty) => {
        editorDirtyRef.current = dirty;
      }}
      onEditorTextChange={(text) => {
        editorTextRef.current = text;
      }}
      outline={outline}
      onEditBlock={handleOutlineEdit}
      openSections={openSections}
      onToggleSection={handleToggleSection}
      isFixedDocx
    />
  );

  const sidebarFooter = (
    <SidebarFooter
      templateSlug={slug}
      documentId={documentId}
      canUndo={!isEditDisabled && canUndo}
      canRedo={!isEditDisabled && canRedo}
      onUndo={handleUndo}
      onRedo={handleRedo}
      isEditable
      signatureFieldCount={signatureFields.length}
      isPremiumUnlocked
      onSendToSignature={
        hideDocumentActions && !isContractApproved
          ? () => setShowApprovalConfirmation(true)
          : handleSendForSignature
      }
      onRequestDownload={handleDownload}
      onRequestProtectedDownload={() => void handleProtectedDownload()}
      isDownloading={isDownloading}
      isDownloadingProtected={isDownloadingProtected}
      hideDownload={hideDocumentActions}
      isApprovalPage={hideDocumentActions}
      isPendingApproval={!hideDocumentActions && isPendingApproval}
      isContractApproved={isContractApproved}
    />
  );

  return (
    <ReviewShell
      scrollContainerRef={scrollRef}
      sidebarOpen={sidebarOpen}
      onSidebarOpenChange={setSidebarOpen}
      sidebar={sidebarInner}
      sidebarFooter={isEditing ? undefined : sidebarFooter}
      header={
        <TemplatePDFReviewHeader
          templateTitle={templateTitle}
          templateSlug={slug}
          documentId={documentId}
          numPages={numPages}
          signatureFields={signatureFields}
          signatoriesLength={signatories.length}
          hasUnsavedChanges={false}
          isAuthenticated
          onBack={() => navigate(-1)}
          // A fixed-DOCX contract carries its own headers/footers from the Word
          // template, so letterheads don't apply.
          canLetterhead={false}
          onLetterheadClick={() => undefined}
          setShowDownloadPrompt={() => void handleDownload()}
          handleSendToSignature={
            hideDocumentActions && !isContractApproved
              ? () => setShowApprovalConfirmation(true)
              : handleSendForSignature
          }
          onSaveDraft={() => undefined}
          saveDraftStatus="idle"
          canSaveDraft={false}
          isEditable
          hideDocumentActions={hideDocumentActions}
          isApprovalPage={hideDocumentActions}
          isContractApproved={isContractApproved}
          hasSentForApproval={contract.HasSentForApproval === true}
          handleDownloadPdf={() => void handleDownload()}
          isDownloading={isDownloading}
        />
      }
      toolbar={
        <PdfPreviewToolbar
          scale={appliedScale}
          // While an edit re-renders in the hidden buffer the old page stays
          // on screen — surface the same "Preview updating…" chip the PDF
          // review shows during its double-buffered regeneration.
          isPdfStale={isRerendering}
          isLoadingPdf={false}
          onRegenerateNow={() => undefined}
          onZoomIn={() =>
            setUserZoom((z) => Math.min(MAX_ZOOM, Math.round((z + 0.1) * 100) / 100))
          }
          onZoomOut={() =>
            setUserZoom((z) => Math.max(MIN_ZOOM, Math.round((z - 0.1) * 100) / 100))
          }
          onFitPage={() => setUserZoom(1)}
          onFitWidth={() => setUserZoom(1)}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((o) => !o)}
        />
      }
      mobileActionBar={
        <MobileActionBar
          templateSlug={slug}
          documentId={documentId}
          isEditable
          signatureFieldCount={signatureFields.length}
          onRequestDownload={() => void handleDownload()}
          onSendToSignature={
            hideDocumentActions && !isContractApproved
              ? () => setShowApprovalConfirmation(true)
              : handleSendForSignature
          }
          isDownloading={isDownloading}
          hideDownload={hideDocumentActions}
          isApprovalPage={hideDocumentActions}
          isContractApproved={isContractApproved}
          isPendingApproval={!hideDocumentActions && isPendingApproval}
        />
      }
      banner={
        unanchored.length > 0 ? (
          <div className="mx-4 mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 sm:mx-6">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {unanchored.every((u) => u.reason === 'no-anchor')
                ? `This template doesn't mark where ${unanchored.map((u) => u.label).join(', ')} signs, so ${unanchored.length > 1 ? 'their boxes were' : 'the box was'} placed at the end of the document. Drag ${unanchored.length > 1 ? 'them' : 'it'} onto the right signing line — your placement is saved.`
                : `Couldn't find the signing line for ${unanchored.map((u) => u.label).join(', ')} in this document. Drag ${unanchored.length > 1 ? 'those boxes' : 'that box'} into place — your placement is saved.`}
            </span>
          </div>
        ) : undefined
      }
      overlay={isPreparing ? <PreparingContract /> : undefined}
      dialogs={
        <>
          <ConfirmationModal
            open={showApprovalConfirmation}
            onOpenChange={setShowApprovalConfirmation}
            title={t('GENERATEREVIEW_ACTIONS_APPROVECONTRACT')}
            description={t('GENERATEREVIEW_ACTIONS_APPROVECONTRACT_DESCRIPTION')}
            confirmText="GENERATEREVIEW_ACTIONS_APPROVECONTRACT"
            onConfirm={() => void handleApproveContract()}
            preventAutoClose
            isLoading={isApprovingContract}
          />
          <UnsavedChangesDialog
            open={pendingBlockId !== null}
            onOpenChange={(open) => {
              if (!open) clearPending();
            }}
            onDiscard={handleDiscardAndSwitch}
            onSaveAndSwitch={handleSaveAndSwitch}
          />
        </>
      }
      viewer={
        <>
          {rendering && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/70">
              <Loader className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {renderError ? (
            <div className="p-6 text-sm text-destructive font-semibold">{renderError}</div>
          ) : (
            <div
              ref={overlayRootRef}
              style={{
                height: spacerHeight,
                width: spacerWidth,
                margin: '0 auto',
                // Positioned so the signature layer can sit over the pages
                // *outside* the scale wrapper — see DocxPageOverlayLayer.
                position: 'relative',
              }}
            >
              <div
                ref={scaleWrapRef}
                style={{
                  transform: `scale(${appliedScale})`,
                  transformOrigin: 'top left',
                  width: naturalSize ? naturalSize.w : undefined,
                  position: 'relative',
                }}
              >
                <div
                  ref={docRef}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  onClick={handleDocClick}
                  className={isEditing ? 'select-none' : 'select-none cursor-pointer'}
                />
                {/* Hover + active-edit highlight overlay (visual only). */}
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{ pointerEvents: 'none', zIndex: 10 }}
                >
                  {editRect && (
                    <div
                      style={{
                        position: 'absolute',
                        left: editRect.x,
                        top: editRect.y,
                        width: editRect.w,
                        height: editRect.h,
                        borderRadius: 4,
                        border: '2px solid hsl(var(--selise-blue) / 0.8)',
                        backgroundColor: 'hsl(var(--selise-blue) / 0.12)',
                        boxShadow: '0 1px 10px hsl(var(--selise-blue) / 0.25)',
                      }}
                    />
                  )}
                  {hoverRect && !editRect && (
                    <div
                      style={{
                        position: 'absolute',
                        left: hoverRect.x,
                        top: hoverRect.y,
                        width: hoverRect.w,
                        height: hoverRect.h,
                        borderRadius: 4,
                        border: '2px solid hsl(var(--selise-blue) / 0.45)',
                        backgroundColor: 'hsl(var(--selise-blue) / 0.08)',
                      }}
                    />
                  )}
                  {/* ?anchors=1 — the text each signature box was derived from. */}
                  {anchorDebug.map((a, i) => (
                    <div
                      key={`${a.label}-${i}`}
                      style={{
                        position: 'absolute',
                        left: a.x,
                        top: a.y,
                        width: a.w,
                        height: a.h,
                        border: '2px dashed hsl(var(--mauveine))',
                        backgroundColor: 'hsl(var(--mauveine) / 0.12)',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: -14,
                          left: 0,
                          fontSize: 9,
                          whiteSpace: 'nowrap',
                          color: 'hsl(var(--mauveine))',
                        }}
                      >
                        {a.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Draggable signature fields. Deliberately a sibling of the
                    scaled wrapper, not a child — see DocxPageOverlayLayer. */}
              <DocxPageOverlayLayer
                pages={pageGeometry}
                appliedScale={appliedScale}
                fields={signatureFields}
                signatories={overlaySignatories}
                selectedField={selectedField}
                onSelectField={setSelectedField}
                onFieldsChange={handleSignatureFieldsChange}
              />
            </div>
          )}
        </>
      }
    />
  );
}
